import { usePreferredReducedMotion } from '@vueuse/core'

interface UseParallaxTransformOptions {
  elementTop: Ref<number | null | undefined> | ComputedRef<number | null | undefined>
  elementHeight: Ref<number | null | undefined> | ComputedRef<number | null | undefined>
  viewportHeight: Ref<number | null | undefined> | ComputedRef<number | null | undefined>
  speed?: Ref<number> | ComputedRef<number> | number
  maxOffsetRatio?: Ref<number | null | undefined> | ComputedRef<number | null | undefined> | number | null
  disabled?: Ref<boolean> | ComputedRef<boolean> | boolean
}

interface ParallaxTransformInput {
  elementTop?: number | null
  elementHeight?: number | null
  viewportHeight?: number | null
  speed?: number
  maxOffsetRatio?: number | null
  disabled?: boolean
  reducedMotion?: boolean
}

export function getParallaxTransform({
  elementTop,
  elementHeight,
  viewportHeight,
  speed = 0.4,
  maxOffsetRatio,
  disabled = false,
  reducedMotion = false
}: ParallaxTransformInput) {
  if (disabled || reducedMotion) return undefined

  const top = elementTop ?? 0
  const height = elementHeight ?? 0
  const viewport = viewportHeight && viewportHeight > 0 ? viewportHeight : 1
  const centerOffset = top + height / 2 - viewport / 2
  const normalized = Math.max(-1, Math.min(1, centerOffset / ((viewport + height) / 2)))
  const rawOffset = -Math.round(normalized * height * speed)

  if (maxOffsetRatio === null || maxOffsetRatio === undefined) {
    return `translate3d(0, ${rawOffset}px, 0)`
  }

  const maxOffset = Math.round(height * maxOffsetRatio)
  const offset = Math.max(-maxOffset, Math.min(maxOffset, rawOffset))

  return `translate3d(0, ${offset}px, 0)`
}

export const useParallaxTransform = ({
  elementTop,
  elementHeight,
  viewportHeight,
  speed = 0.4,
  maxOffsetRatio,
  disabled = false
}: UseParallaxTransformOptions) => {
  const preferredReducedMotion = usePreferredReducedMotion()

  return computed(() =>
    getParallaxTransform({
      elementTop: unref(elementTop),
      elementHeight: unref(elementHeight),
      viewportHeight: unref(viewportHeight),
      speed: unref(speed),
      maxOffsetRatio: unref(maxOffsetRatio),
      disabled: unref(disabled),
      reducedMotion: preferredReducedMotion.value === 'reduce'
    })
  )
}
